import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingDown, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ProviderSentimentChip,
  NewsSourceMix,
  NewsSentimentSummary,
  MassiveInsightReasoning,
  KeywordChipRow,
  ActiveKeywordFilter,
} from '@/components/news/NewsBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_STYLES = {
  Dividend: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  Analyst: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'Company News': 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
};

function ArticleCard({ article, selectedKeyword, onToggleKeyword }) {
  const handleClick = () => {
    if (article.url) {
      window.open(article.url, '_blank', 'noopener,noreferrer');
    } else {
      window.open(`https://www.google.com/search?q=${encodeURIComponent(article.title)}`, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="rounded-lg border bg-card px-3 py-2 cursor-pointer hover:border-primary/40 transition-colors group"
      onClick={handleClick}
      data-testid="news-article-card"
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${CATEGORY_STYLES[article.category] || CATEGORY_STYLES['Company News']}`}>
          {article.category}
        </span>
        <ProviderSentimentChip provider={article.provider} sentiment={article.sentiment} />
        <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1">
          {article.age}
          <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
      </div>
      <h4 className="text-sm font-bold leading-snug mb-0.5 group-hover:text-primary transition-colors">{article.title}</h4>
      <KeywordChipRow article={article} selected={selectedKeyword} onToggle={onToggleKeyword} />
      {article.summary && (
        <p className="text-xs font-bold text-muted-foreground leading-snug mb-1 line-clamp-2 mt-1">{article.summary}</p>
      )}
      <MassiveInsightReasoning article={article} />
      <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1.5 flex-wrap">
        <span>— {article.source}</span>
      </p>
    </div>
  );
}

export default function NewsTab({ ticker, prefetchedData, currentPrice }) {
  const [data, setData] = useState(prefetchedData || null);
  const [loading, setLoading] = useState(!prefetchedData);
  const [refreshing, setRefreshing] = useState(false);
  // Round 92o++++++++N++++++++ — keyword-filter state. Clicking a
  // Massive `#keyword` chip narrows the article list to only articles
  // whose keywords array contains that token (case-insensitive).
  // Yahoo/Finnhub articles never carry keywords, so they only appear
  // when no filter is active.
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  // Clear the filter whenever the user switches stocks. Avoids the
  // user landing on a new stock with a stale filter and seeing zero
  // articles for no obvious reason.
  useEffect(() => { setSelectedKeyword(null); }, [ticker]);

  useEffect(() => {
    if (prefetchedData) {
      setData(prefetchedData);
      setLoading(false);
    }
  }, [prefetchedData]);

  useEffect(() => {
    if (!ticker || prefetchedData) return;
    setLoading(true);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticker, prefetchedData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: fresh } = await axios.get(`${API}/stocks/${ticker}/news?refresh=1`, { withCredentials: true });
      setData(fresh);
    } catch {}
    setRefreshing(false);
  };

  // Color the analyst consensus text
  const getConsensusStyle = (consensus) => {
    const c = (consensus || '').toLowerCase();
    if (c.includes('strong buy') || c === 'buy') return 'text-green-600 dark:text-green-400';
    if (c.includes('sell')) return 'text-red-600 dark:text-red-400';
    if (c.includes('hold')) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-foreground';
  };

  // Color the price target vs current price
  // Green: target > +3% of current
  // Yellow: target within ±3% of current
  // Red: target < -3% of current
  const getTargetStyle = () => {
    const tgt = Number(data?.price_target) || 0;
    const cur = Number(currentPrice) || 0;
    if (!tgt || !cur) return 'text-foreground';
    const diffPct = ((tgt - cur) / cur) * 100;
    if (diffPct > 3) return 'text-green-600 dark:text-green-400';
    if (diffPct < -3) return 'text-red-600 dark:text-red-400';
    return 'text-yellow-600 dark:text-yellow-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading news...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[200px]" data-testid="news-error">
        <div className="text-center text-muted-foreground">
          <TrendingDown className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">No news available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="news-tab">
      {/* Analyst Consensus */}
      {data.consensus && data.consensus !== 'N/A' && (
        <div className="rounded-lg border bg-card px-3 py-2 flex items-center justify-between" data-testid="analyst-consensus">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Analyst Consensus</p>
            <p className={`text-base font-bold ${getConsensusStyle(data.consensus)}`} data-testid="consensus-value">{data.consensus}</p>
          </div>
          {data.price_target > 0 && (
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Price Target</p>
              <p className={`text-base font-bold font-mono ${getTargetStyle()}`} data-testid="price-target-value">${data.price_target.toFixed(2)}</p>
            </div>
          )}
        </div>
      )}

      {/* Articles */}
      {data.articles && data.articles.length > 0 ? (
        (() => {
          const allArticles = data.articles;
          const filtered = selectedKeyword
            ? allArticles.filter((a) => {
                const kws = Array.isArray(a.keywords) ? a.keywords : [];
                return kws.some(
                  (k) => (k || '').toLowerCase() === selectedKeyword.toLowerCase()
                );
              })
            : allArticles;
          return (
            <>
              <div className="pb-1 flex items-center justify-between gap-2 flex-wrap">
                <NewsSourceMix articles={allArticles} />
                <NewsSentimentSummary articles={allArticles} />
              </div>
              <ActiveKeywordFilter
                keyword={selectedKeyword}
                matchCount={filtered.length}
                totalCount={allArticles.length}
                onClear={() => setSelectedKeyword(null)}
              />
              {filtered.length === 0 ? (
                <div className="rounded-lg border bg-card p-4 text-center text-muted-foreground text-xs">
                  No articles match #{selectedKeyword}. Clear the filter to see all news.
                </div>
              ) : (
                filtered.map((article, i) => (
                  <ArticleCard
                    key={i}
                    article={article}
                    selectedKeyword={selectedKeyword}
                    onToggleKeyword={setSelectedKeyword}
                  />
                ))
              )}
            </>
          );
        })()
      ) : (
        <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground text-sm" data-testid="no-news-message">
          No current news for {ticker} at this time.
        </div>
      )}

      {/* Refresh */}
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
        data-testid="refresh-news-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing...' : 'Refresh news'}
      </button>
    </div>
  );
}
